import type { TutorBillingRow } from '../model/interfaces'
import type { TutorBillingChangeRequestAction } from '../model/types'
import type { AppRequestRoute } from '@shared/config'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useTutorBillingStore } from '../model/store'

const CHANGE_REQUEST_ACTION_ROUTES: Record<TutorBillingChangeRequestAction, AppRequestRoute> = {
  accept: 'admin.tutors.billing.change-requests.accept',
  reject: 'admin.tutors.billing.change-requests.reject',
  'confirm-salesforce': 'admin.tutors.billing.change-requests.confirm-salesforce',
}

export const useTutorBillingChangeRequestAction = async (
  changeRequestId: number,
  action: TutorBillingChangeRequestAction,
): Promise<TutorBillingRow | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const store = useTutorBillingStore()

  const url = useAdminRoutes(CHANGE_REQUEST_ACTION_ROUTES[action], { change_request_id: changeRequestId })

  store.setSaving(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const row: TutorBillingRow = data.data.billing

    store.replaceRow(row)
    toast.success(data.message)

    return row
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    store.setSaving(false)
  }
}
